import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Container, 
  Dialog,
  DialogActions,
  DialogContent, 
  DialogTitle,
  Grid, 
  IconButton,
  Paper, 
  TextField,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Chip,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import ScanProgressDisplay from './ScanProgressDisplay';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Image as ImageIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import apiClient from '../api/index';
import { 
  getImageCollections, getImageCollection, createImageCollection, 
  updateImageCollection, deleteImageCollection, scanDirectory, getImageFiles,
  getCollectionPermissions, addPermission, removePermission
} from '../api/images';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ImageCollections = () => {
  const { user, authToken } = useAuth();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', 'scan', 'permission'
  const [formData, setFormData] = useState({
    name: '',
    path: '',
    description: '',
    patterns: ''
  });
  const [scanData, setScanData] = useState({
    rescanExisting: false
  });
  const [permissionData, setPermissionData] = useState({
    roleId: '',
    permissionType: 'read'
  });
  const [scanJobId, setScanJobId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [searchParams, setSearchParams] = useState({
    filter: '',
    raMin: '',
    raMax: '',
    decMin: '',
    decMax: '',
    obsdateMin: '',
    obsdateMax: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Get collections on component mount
  useEffect(() => {
    fetchCollections();
    if (user?.is_staff || user?.is_superuser) {
      fetchRoles();
    }
  }, [user]);

  // Fetch collections from API
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const data = await getImageCollections();
      setCollections(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError('Failed to fetch image collections');
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles (for permissions)
  const fetchRoles = async () => {
    try {
      const response = await apiClient.get(`/adss/v1/users/roles`);
      setRoles(response.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  // Fetch permissions for a collection
  const fetchPermissions = async (collectionId) => {
    try {
      const data = await getCollectionPermissions(collectionId);
      setPermissions(data);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      showSnackbar('Failed to fetch permissions', 'error');
    }
  };

  // Fetch image files for a collection
  const fetchImageFiles = async (collectionId) => {
    try {
      const options = {};
      if (searchParams.filter) options.filter_str = searchParams.filter;
      if (searchParams.raMin && searchParams.raMax) {
        options.ra_min = searchParams.raMin;
        options.ra_max = searchParams.raMax;
      }
      if (searchParams.decMin && searchParams.decMax) {
        options.dec_min = searchParams.decMin;
        options.dec_max = searchParams.decMax;
      }
      if (searchParams.obsdateMin) {
        options.obsdate_min = searchParams.obsdateMin;
      }
      if (searchParams.obsdateMax) {
        options.obsdate_max = searchParams.obsdateMax;
      }

      const data = await getImageFiles(collectionId, options);
      setImageFiles(data);
    } catch (err) {
      console.error('Error fetching image files:', err);
      showSnackbar('Failed to fetch image files', 'error');
    }
  };

  // Create a new collection
  const createCollection = async () => {
    try {
      let patterns = null;
      if (formData.patterns && formData.patterns.trim()) {
        try {
          patterns = JSON.parse(formData.patterns);
        } catch (e) {
          showSnackbar('Invalid JSON format in patterns field', 'error');
          return;
        }
      }

      const newCollection = await createImageCollection(formData, patterns);
      setCollections([...collections, newCollection]);
      setOpenDialog(false);
      showSnackbar('Collection created successfully', 'success');
    } catch (err) {
      console.error('Error creating collection:', err);
      showSnackbar(
        `Failed to create collection: ${err.response?.data?.detail || err.message}`, 
        'error'
      );
    }
  };

  // Update a collection
  const updateCollection = async () => {
    try {
      // Parse patterns JSON if provided
      let patterns = null;
      if (formData.patterns && formData.patterns.trim()) {
        try {
          patterns = JSON.parse(formData.patterns);
        } catch (e) {
          showSnackbar('Invalid JSON format in patterns field', 'error');
          return;
        }
      }

      const updatedCollection = await updateImageCollection(
        selectedCollection.id,
        {
          name: formData.name,
          description: formData.description
        },
        patterns
      );
      // Update collections list
      setCollections(collections.map(c => 
        c.id === selectedCollection.id ? updatedCollection : c
      ));
      setOpenDialog(false);
      showSnackbar('Collection updated successfully', 'success');
    } catch (err) {
      console.error('Error updating collection:', err);
      showSnackbar(
        `Failed to update collection: ${err.response?.data?.detail || err.message}`, 
        'error'
      );
    }
  };

  // Delete a collection
  const deleteCollection = async (id) => {
    // Ask for confirmation
    if (!window.confirm('Are you sure you want to delete this collection? All associated files will be removed from the database.')) {
      return;
    }
    
    try {
      await deleteImageCollection(id);
      // Update collections list
      setCollections(collections.filter(c => c.id !== id));
      if (selectedCollection && selectedCollection.id === id) {
        setSelectedCollection(null);
      }
      showSnackbar('Collection deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting collection:', err);
      showSnackbar(
        `Failed to delete collection: ${err.response?.data?.detail || err.message}`, 
        'error'
      );
    }
  };

  // Scan a collection for image files
  const scanCollection = async () => {
    try {
      setLoading(true);
      const result = await scanDirectory(
        selectedCollection.id, 
        scanData.rescanExisting
      );
      
      // Store the job ID and change dialog mode to show progress
      setScanJobId(result.id);
      setDialogMode('scan-progress');
      
    } catch (err) {
      console.error('Error starting scan:', err);
      showSnackbar(
        `Failed to start scan: ${err.response?.data?.detail || err.message}`, 
        'error'
      );
      setLoading(false);
    }
  };
  
  // Handle scan job completion
  const handleScanComplete = (result) => {
    // Close the dialog
    setOpenDialog(false);
    
    // Show completion message
    showSnackbar(
      `Scan complete: Found ${result.total_files_found} files, ` +
      `Added ${result.new_files_added} new files, ` +
      `Updated ${result.existing_files_updated} existing files, ` +
      `Failed to process ${result.failed_files} files`,
      'success'
    );
    
    // Refresh image files list
    fetchImageFiles(selectedCollection.id);
    
    // Reset scan job ID and loading state
    setScanJobId(null);
    setLoading(false);
  };

  // Add a permission for a role
  const addPermissionHandler = async () => {
    try {
      await addPermission(
        selectedCollection.id,
        parseInt(permissionData.roleId),
        permissionData.permissionType
      );
      // Update permissions list
      fetchPermissions(selectedCollection.id);
      setOpenDialog(false);
      showSnackbar('Permission added successfully', 'success');
    } catch (err) {
      console.error('Error adding permission:', err);
      showSnackbar(
        `Failed to add permission: ${err.response?.data?.detail || err.message}`, 
        'error'
      );
    }
  };

  // Remove a permission
  const removePermissionHandler = async (permissionId) => {
    try {
      await removePermission(permissionId);
      // Update permissions list
      fetchPermissions(selectedCollection.id);
      showSnackbar('Permission removed successfully', 'success');
    } catch (err) {
      console.error('Error removing permission:', err);
      showSnackbar(
        `Failed to remove permission: ${err.response?.data?.detail || err.message}`, 
        'error'
      );
    }
  };

  // Handle dialog open
  const handleOpenDialog = (mode, collection = null) => {
    setDialogMode(mode);
    setSelectedCollection(collection);
    
    if (mode === 'create') {
      setFormData({ name: '', path: '', description: '', patterns: '' });
    } else if (mode === 'edit' && collection) {
      setFormData({
        name: collection.name,
        path: collection.path,
        description: collection.description || '',
        patterns: collection.patterns ? JSON.stringify(collection.patterns, null, 2) : ''
      });
    } else if (mode === 'scan' && collection) {
      setScanData({ rescanExisting: false });
    } else if (mode === 'permission' && collection) {
      setPermissionData({ roleId: '', permissionType: 'read' });
      fetchPermissions(collection.id);
    }
    
    setOpenDialog(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (dialogMode === 'create' || dialogMode === 'edit') {
      setFormData({
        ...formData,
        [name]: value
      });
    } else if (dialogMode === 'scan') {
      setScanData({
        ...scanData,
        [name]: type === 'checkbox' ? checked : value
      });
    } else if (dialogMode === 'permission') {
      setPermissionData({
        ...permissionData,
        [name]: value
      });
    }
  };

  // Handle search params changes
  const handleSearchParamChange = (e) => {
    const { name, value } = e.target;
    setSearchParams({
      ...searchParams,
      [name]: value
    });
  };

  // Handle search submission
  const handleSearch = () => {
    if (selectedCollection) {
      fetchImageFiles(selectedCollection.id);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (dialogMode === 'create') {
      createCollection();
    } else if (dialogMode === 'edit') {
      updateCollection();
    } else if (dialogMode === 'scan') {
      scanCollection();
    } else if (dialogMode === 'permission') {
      addPermissionHandler();
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    if (selectedCollection) {
      if (newValue === 1) {
        // Files tab
        fetchImageFiles(selectedCollection.id);
      } else if (newValue === 2) {
        // Permissions tab
        fetchPermissions(selectedCollection.id);
      }
    }
  };

  // Handle collection selection
  const handleCollectionSelect = (collection) => {
    setSelectedCollection(collection);
    setTabValue(0); // Reset to info tab
  };

  // Show snackbar message
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Get role name from ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : 'Unknown Role';
  };

  if (loading && collections.length === 0) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Image Collections
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Manage astronomical image collections and scan FITS files for metadata.
        </Typography>
        
        {user?.is_staff && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
            sx={{ mb: 2 }}
          >
            Add Collection
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Collections list */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Available Collections
            </Typography>
            
            {collections.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No image collections found.
              </Typography>
            ) : (
              <Box>
                {collections.map((collection) => (
                  <Card 
                    key={collection.id} 
                    sx={{ 
                      mb: 2, 
                      cursor: 'pointer',
                      bgcolor: selectedCollection?.id === collection.id ? 'action.selected' : 'background.paper'
                    }}
                    onClick={() => handleCollectionSelect(collection)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <FolderIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6" component="div">
                          {collection.name}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Path: {collection.path}
                      </Typography>
                      
                      {collection.description && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {collection.description}
                        </Typography>
                      )}
                      
                      <Typography variant="caption" color="textSecondary">
                        Last scanned: {collection.last_scanned ? 
                          new Date(collection.last_scanned).toLocaleString() : 
                          'Never'
                        }
                      </Typography>
                      
                      {user?.is_staff && (
                        <Box mt={1} display="flex" justifyContent="flex-end">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog('edit', collection);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog('scan', collection);
                            }}
                          >
                            <SearchIcon />
                          </IconButton>
                          
                          {user?.is_superuser && (
                            <>
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog('permission', collection);
                                }}
                              >
                                <SecurityIcon />
                              </IconButton>
                              
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteCollection(collection.id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Collection details */}
        <Grid item xs={12} md={8}>
          {selectedCollection ? (
            <Paper elevation={2} sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
              <Box mb={2}>
                <Typography variant="h5" component="h2">
                  {selectedCollection.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedCollection.path}
                </Typography>
              </Box>
              
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Info" />
                  <Tab label="Files" />
                  {user?.is_staff && <Tab label="Permissions" />}
                </Tabs>
              </Box>
              
              {/* Info Tab */}
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Description</Typography>
                    <Typography variant="body2">
                      {selectedCollection.description || 'No description provided'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1">Created At</Typography>
                    <Typography variant="body2">
                      {new Date(selectedCollection.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1">Last Updated</Typography>
                    <Typography variant="body2">
                      {new Date(selectedCollection.updated_at).toLocaleString()}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1">Last Scanned</Typography>
                    <Typography variant="body2">
                      {selectedCollection.last_scanned ? 
                        new Date(selectedCollection.last_scanned).toLocaleString() : 
                        'Never'
                      }
                    </Typography>
                  </Grid>
                  
                  {user?.is_staff && (
                    <Grid item xs={12} mt={2}>
                      <Button 
                        variant="outlined"
                        startIcon={<SearchIcon />}
                        onClick={() => handleOpenDialog('scan', selectedCollection)}
                      >
                        Scan for Images
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>
              
              {/* Files Tab */}
              <TabPanel value={tabValue} index={1}>
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Image Files
                  </Typography>
                  
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        name="filter"
                        label="Filename Filter"
                        value={searchParams.filter}
                        onChange={handleSearchParamChange}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6} sm={2}>
                      <TextField
                        name="raMin"
                        label="RA Min"
                        type="number"
                        value={searchParams.raMin}
                        onChange={handleSearchParamChange}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6} sm={2}>
                      <TextField
                        name="raMax"
                        label="RA Max"
                        type="number"
                        value={searchParams.raMax}
                        onChange={handleSearchParamChange}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6} sm={2}>
                      <TextField
                        name="decMin"
                        label="Dec Min"
                        type="number"
                        value={searchParams.decMin}
                        onChange={handleSearchParamChange}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6} sm={2}>
                      <TextField
                        name="decMax"
                        label="Dec Max"
                        type="number"
                        value={searchParams.decMax}
                        onChange={handleSearchParamChange}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <TextField
                        name="obsdateMin"
                        label="Obs Date Min"
                        type="date"
                        value={searchParams.obsdateMin}
                        onChange={handleSearchParamChange}
                        size="small"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                        helperText="Minimum observation date"
                      />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <TextField
                        name="obsdateMax"
                        label="Obs Date Max"
                        type="date"
                        value={searchParams.obsdateMax}
                        onChange={handleSearchParamChange}
                        size="small"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                        helperText="Maximum observation date"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        size="small"
                      >
                        Search
                      </Button>
                    </Grid>
                  </Grid>
                  
                  {loading ? (
                    <Box display="flex" justifyContent="center" my={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {imageFiles.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          No image files found. Try scanning the collection or adjusting your search parameters.
                        </Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Filename</TableCell>
                                <TableCell>RA (deg)</TableCell>
                                <TableCell>Dec (deg)</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Filter</TableCell>
                                <TableCell>Obs Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {imageFiles.map((file) => (
                                <TableRow key={file.id}>
                                  <TableCell>{file.filename}</TableCell>
                                  <TableCell>{file.ra_center?.toFixed(5) || 'N/A'}</TableCell>
                                  <TableCell>{file.dec_center?.toFixed(5) || 'N/A'}</TableCell>
                                  <TableCell>{file.width && file.height ? `${file.width}×${file.height}` : 'N/A'}</TableCell>
                                  <TableCell>{file.filter || 'N/A'}</TableCell>
                                  <TableCell>
                                    {file.date_obs ? new Date(file.date_obs).toLocaleDateString() : 'N/A'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </>
                  )}
                </Box>
              </TabPanel>
              
              {/* Permissions Tab */}
              <TabPanel value={tabValue} index={2}>
                {user?.is_staff && (
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                      Access Permissions
                    </Typography>
                    
                    {user?.is_superuser && (
                      <Button
                        variant="outlined"
                        startIcon={<SecurityIcon />}
                        onClick={() => handleOpenDialog('permission', selectedCollection)}
                        sx={{ mb: 2 }}
                      >
                        Add Permission
                      </Button>
                    )}
                    
                    {permissions.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No permissions found. Add permissions to allow roles to access this collection.
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Role</TableCell>
                              <TableCell>Permission Type</TableCell>
                              <TableCell>Created At</TableCell>
                              {user?.is_superuser && <TableCell>Actions</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {permissions.map((permission) => (
                              <TableRow key={permission.id}>
                                <TableCell>{getRoleName(permission.role_id)}</TableCell>
                                <TableCell>{permission.permission_type}</TableCell>
                                <TableCell>
                                  {new Date(permission.created_at).toLocaleString()}
                                </TableCell>
                                {user?.is_superuser && (
                                  <TableCell>
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => removePermission(permission.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </TabPanel>
            </Paper>
          ) : (
            <Paper 
              elevation={2} 
              sx={{ 
                p: 4, 
                height: '70vh', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'action.hover'
              }}
            >
              <FolderOpenIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Select a collection to view details
              </Typography>
              {user?.is_staff && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('create')}
                  sx={{ mt: 2 }}
                >
                  Add New Collection
                </Button>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Dialogs */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {dialogMode === 'create' && 'Add New Image Collection'}
            {dialogMode === 'edit' && 'Edit Image Collection'}
            {dialogMode === 'scan' && 'Scan Directory for FITS Files'}
            {dialogMode === 'scan-progress' && 'Scanning Directory'}
            {dialogMode === 'permission' && 'Add Collection Permission'}
          </DialogTitle>
          
          <DialogContent>
            {/* Create/Edit Collection Form */}
            {(dialogMode === 'create' || dialogMode === 'edit') && (
              <>
                <TextField
                  name="name"
                  label="Collection Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                />
                
                {dialogMode === 'create' && (
                  <TextField
                    name="path"
                    label="Directory Path"
                    value={formData.path}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    margin="normal"
                    helperText="Absolute path to the directory containing FITS files"
                  />
                )}
                
                <TextField
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                />
                
                <TextField
                  name="patterns"
                  label="Patterns (JSON)"
                  value={formData.patterns}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={4}
                  margin="normal"
                  helperText="JSON object with file pattern configurations for searching and filtering. Leave empty if not needed."
                  placeholder='{"filter_pattern": "*.fits", "naming_convention": "standard"}'
                />
              </>
            )}
            
            {/* Scan Directory Form */}
            {dialogMode === 'scan' && (
              <>
                <Typography variant="body1" paragraph>
                  This will scan the directory at <strong>{selectedCollection?.path}</strong> for FITS files and extract metadata.
                </Typography>
                
                <Typography variant="body2" color="textSecondary" paragraph>
                  Scanning may take some time depending on the number of files.
                </Typography>
                
                <Box display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="rescanExisting"
                        checked={scanData.rescanExisting}
                        onChange={handleInputChange}
                      />
                    }
                    label="Rescan files that are already in the database"
                  />
                </Box>
              </>
            )}
            
            {/* Scan Progress Display */}
            {dialogMode === 'scan-progress' && scanJobId && (
              <Box p={2}>
                <ScanProgressDisplay 
                  jobId={scanJobId}
                  onComplete={handleScanComplete}
                />
                {loading && (
                  <Typography variant="body2" color="textSecondary" mt={2}>
                    Scan is running in the background. You can close this dialog and check back later.
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Add Permission Form */}
            {dialogMode === 'permission' && (
              <>
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>Current Permissions</Typography>
                  
                  {permissions.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No permissions assigned yet. Add permissions below to allow roles to access this collection.
                    </Typography>
                  ) : (
                    <Box mb={3}>
                      <Paper variant="outlined" sx={{ p: 1, maxHeight: '200px', overflow: 'auto' }}>
                        {permissions.map((permission) => (
                          <Box 
                            key={permission.id} 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              borderBottom: '1px solid #eee',
                              '&:last-child': { borderBottom: 'none' }
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle2">{getRoleName(permission.role_id)}</Typography>
                              <Chip 
                                size="small" 
                                label={permission.permission_type} 
                                color={
                                  permission.permission_type === 'admin' ? 'error' : 
                                  permission.permission_type === 'write' ? 'warning' : 
                                  'success'
                                }
                                sx={{ mr: 1 }}
                              />
                            </Box>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => removePermissionHandler(permission.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>Add New Permission</Typography>
                </Box>
                
                <TextField
                  name="roleId"
                  label="Role"
                  select
                  value={permissionData.roleId}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                  SelectProps={{
                    native: true
                  }}
                >
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </TextField>
                
                <TextField
                  name="permissionType"
                  label="Permission Type"
                  select
                  value={permissionData.permissionType}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                  SelectProps={{
                    native: true
                  }}
                >
                  <option value="read">Read</option>
                  <option value="write">Write</option>
                  <option value="admin">Admin</option>
                </TextField>
                
                <Box mt={2} p={2} bgcolor="action.hover" borderRadius={1}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Permission Types:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Read:</strong> Users can view collections and download files<br />
                    <strong>Write:</strong> Users can scan directories and update metadata<br />
                    <strong>Admin:</strong> Users can manage collections and permissions
                  </Typography>
                </Box>
              </>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>
              {dialogMode === 'scan-progress' ? 'Close' : 'Cancel'}
            </Button>
            {dialogMode !== 'scan-progress' && (
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : (
                  dialogMode === 'create' ? 'Create' : 
                  dialogMode === 'edit' ? 'Update' : 
                  dialogMode === 'scan' ? 'Start Scan' : 
                  'Add Permission'
                )}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ImageCollections;